import axios, { AxiosProgressEvent, InternalAxiosRequestConfig } from 'axios';
import {
  ApiResponse,
  UploadResponse,
  ProcessResponse,
  JobStatusResponse,
  ProcessingConfig
} from '../types';
import { supabase } from '../lib/supabase';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 0, // No timeout by default (will be set per-request as needed)
});

// Add request interceptor to include JWT token from Supabase session
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // Get current session from Supabase with timeout
      const sessionPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Session fetch timeout')), 5000)
      );

      const { data: { session } } = await Promise.race([
        sessionPromise,
        timeoutPromise
      ]) as any;

      if (session?.access_token) {
        // Add Authorization header with Bearer token
        config.headers.Authorization = `Bearer ${session.access_token}`;
      }
    } catch (error) {
      // If session fetch fails or times out, continue without auth
      // This prevents blocking uploads/requests in Chrome
      console.warn('Failed to get Supabase session, continuing without auth:', error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Helper function to sleep for retry delays
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to retry failed requests
async function retryRequest<T>(
  fn: () => Promise<T>,
  retries: number = 3,
  delayMs: number = 1000
): Promise<T> {
  let lastError: any;

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;

      // Check if it's a timeout or network error
      const isRetryable =
        error.code === 'ECONNABORTED' ||
        error.code === 'ERR_NETWORK' ||
        error.message?.includes('timeout') ||
        error.message?.includes('Network Error') ||
        !error.response; // No response = network/timeout issue

      if (!isRetryable || attempt === retries - 1) {
        throw error;
      }

      console.log(`Request failed (attempt ${attempt + 1}/${retries}), retrying in ${delayMs}ms...`);
      await sleep(delayMs);

      // Exponential backoff: increase delay for next retry
      delayMs = Math.min(delayMs * 1.5, 5000);
    }
  }

  throw lastError;
}

export class ApiService {
  // Upload audio file using signed URLs (direct to GCS - no size limits!)
  static async uploadTarget(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<UploadResponse> {
    try {
      // Step 1: Get signed upload URL from backend
      const urlResponse = await api.post<ApiResponse<{
        uploadUrl: string;
        fileId: string;
        filename: string;
        gcsPath: string;
      }>>(`/upload/generate-url?filename=${encodeURIComponent(file.name)}`, {}, {
        timeout: 60000, // 60 seconds to handle Cloud Run cold starts
      });

      if (!urlResponse.data.success) {
        throw new Error(urlResponse.data.error || 'Failed to get upload URL');
      }

      const { uploadUrl, fileId, filename } = urlResponse.data.data!;

      // Step 2: Upload directly to GCS using signed URL
      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total && onProgress) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
        timeout: 0, // No timeout for large file uploads
      });

      if (onProgress) onProgress(100);

      console.log(`✅ Uploaded ${filename} to GCS (${(file.size / 1024 / 1024).toFixed(1)}MB)`);

      return {
        fileId,
        filename,
        size: file.size
      };
    } catch (error: any) {
      console.error('Upload error:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Upload failed');
    }
  }

  // Upload reference audio file directly to GCS
  static async uploadReference(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<UploadResponse> {
    // Use same upload logic as target
    return this.uploadTarget(file, onProgress);
  }

  // Start processing job
  static async startProcessing(
    targetFileId: string,
    referenceFileId: string,
    config: ProcessingConfig,
    referenceFileId2?: string,
    referenceBlend?: number
  ): Promise<ProcessResponse> {
    const response = await api.post<ApiResponse<ProcessResponse>>('/process', {
      targetFileId,
      referenceFileId,
      referenceFileId2,
      referenceBlend,
      config,
    }, {
      timeout: 30000, // 30 seconds for starting processing (just validates and queues)
    });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Processing failed to start');
    }

    return response.data.data!;
  }

  // Get job status with automatic retry on timeout
  static async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    return retryRequest(
      async () => {
        const response = await api.get<ApiResponse<JobStatusResponse>>(`/status/${jobId}`, {
          timeout: 360000, // 360 seconds (6 minutes) per attempt to handle full processing time
        });

        if (!response.data.success) {
          throw new Error(response.data.error || 'Failed to get job status');
        }

        return response.data.data!;
      },
      3, // 3 retries
      2000 // Start with 2 second delay
    );
  }

  // Cancel processing job
  static async cancelJob(jobId: string): Promise<void> {
    const response = await api.delete<ApiResponse>(`/cancel/${jobId}`, {
      timeout: 10000, // 10 seconds for cancel
    });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to cancel job');
    }
  }

  // Run analysis on completed job
  static async runAnalysis(jobId: string): Promise<void> {
    const response = await api.post<ApiResponse>(`/analyze/${jobId}`, {}, {
      timeout: 30000, // 30 seconds for starting analysis
    });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to start analysis');
    }
  }

  // Download processed file using signed URLs (direct from GCS - no size limits!)
  static async downloadFile(jobId: string, filename: string): Promise<Blob> {
    try {
      // Step 1: Get signed download URL from backend
      const urlResponse = await api.get<ApiResponse<{
        downloadUrl: string;
        filename: string;
      }>>(`/download/generate-url/${jobId}/${filename}`, {
        timeout: 60000, // 60 seconds to handle Cloud Run cold starts
      });

      if (!urlResponse.data.success) {
        throw new Error(urlResponse.data.error || 'Failed to get download URL');
      }

      const { downloadUrl } = urlResponse.data.data!;

      // Step 2: Download directly from GCS using signed URL
      const response = await axios.get(downloadUrl, {
        responseType: 'blob',
        timeout: 0, // No timeout for large file downloads
      });

      console.log(`✅ Downloaded ${filename} from GCS`);

      return response.data;
    } catch (error: any) {
      console.error('Download error:', error);
      throw new Error(error.response?.data?.detail || error.message || 'Download failed');
    }
  }

  // Get file info for preview
  static async getFileInfo(fileId: string) {
    const response = await api.get<ApiResponse>(`/file/${fileId}/info`, {
      timeout: 10000, // 10 seconds for file info
    });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get file info');
    }

    return response.data.data;
  }

  // Get audio preview URL
  static getPreviewUrl(fileId: string): string {
    return `${API_BASE_URL}/preview/${fileId}`;
  }
}

export default ApiService;